import {
  apiContainer,
  queryBuilder,
  massageSingleRecordBeforeUpsert
} from './apiContainer'
import { ArtificialDetailPropertyNames } from '../../common/globalNotary'
import uuid from 'react-uuid'

const SLICE_API_ROOT = '/entities'

const entityApi = apiContainer.injectEndpoints({
  endpoints: build => ({
    listEntities: build.query({
      query: args => queryBuilder(SLICE_API_ROOT, args)
    }),
    listEntityTypes: build.query({
      query: () => `${SLICE_API_ROOT}/~/types`,
      keepUnusedDataFor: 86400,
    }),

    listEntityRoles: build.query({
      query: () => `${SLICE_API_ROOT}/~/roles`,
      keepUnusedDataFor: 86400
    }),
    getOneFatEntity: build.query({
      async queryFn (_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const callArray = [
          fetchWithBQ(`${SLICE_API_ROOT}/${_arg.id}`),
          fetchWithBQ('/groups?entity_id=' + _arg.id)
        ]

        const primaryRes = await Promise.all(callArray)
        const primaryData = primaryRes[0].data.data
        const groups = primaryRes[1].data.data

        const listOfOwnerIds = primaryData.owners.map(o => o.id)
        const listOfContactIds = primaryData.contacts.map(o => o.id)

        const auxArray = [
          fetchWithBQ(`/datazones?entity_id=${_arg.id}&type=commercial`),
          listOfOwnerIds.length
            ? fetchWithBQ(queryBuilder('/parties', { ids: listOfOwnerIds }))
            : Promise.resolve([]),
          listOfContactIds.length
            ? fetchWithBQ(queryBuilder('/parties', { ids: listOfContactIds }))
            : Promise.resolve([]),
          fetchWithBQ(`/assets?entity_id=${_arg.id}`),
          fetchWithBQ(queryBuilder(`/contracts`, { entity_ids: [_arg.id] })),
        ]

        const auxiliaryRes = await Promise.all(auxArray)

        const datazones = auxiliaryRes[0]?.data?.data ?? []
        const fullOwners = (auxiliaryRes[1]?.data?.data ?? [])
          .filter(f => {
            return primaryData.owners.some(o => {
              return f.id === o.id
            })
          })
          .map(f => {
            const match = primaryData.owners.find(c => {
              return c.id === f.id
            })
            f.role_id = match.role_id
            f.name = `${f.first_name} ${f.last_name}`.trim()
            f.type = f.base_type

            return f
          })

        const fullContacts = (auxiliaryRes[2]?.data?.data ?? [])
          .filter(f => {
            return primaryData.contacts.some(o => {
              return f.id === o.id
            })
          })
          .map(f => {
            const match = primaryData.contacts.find(c => {
              return c.id === f.id
            })
            f.role_id = match.role_id
            f.name = `${f.first_name} ${f.last_name}`.trim()
            f.type = f.base_type
            return f
          })

        const assets = auxiliaryRes[3]?.data?.data ?? []
        const contracts = auxiliaryRes[4]?.data?.data ?? []

        primaryData.fullContacts = fullContacts
        primaryData.fullOwners = fullOwners

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1] = groups
        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
        ] = groups

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2] =
          datazones

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
        ] = datazones

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3] = assets

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
        ] = assets

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4] = contracts

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
        ] = contracts

        // tmp fix for saving auxiliary updates
        return { data: { data: { ...primaryData, _objectId: uuid() } } }
      }
    }),
    getExecutiveEntityQuickView: build.query({
      async queryFn (_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const [detailResult, countsResult] = await Promise.all([
          fetchWithBQ(`${SLICE_API_ROOT}/${_arg.id}`),
          fetchWithBQ(queryBuilder(SLICE_API_ROOT, {
            ids: [_arg.id],
            include: ['counts'],
            per_page: 1
          }))
        ])

        if (detailResult.error) return { error: detailResult.error }
        if (countsResult.error) return { error: countsResult.error }

        const entity = detailResult.data?.data
        const counts = countsResult.data?.data?.[0]?.stats ?? []
        return { data: { ...entity, stats: counts } }
      }
    }),
    bulkUpdateEntities: build.mutation({
      query: args => {
        return {
          url: SLICE_API_ROOT,
          method: 'PATCH',
          body: { data: args }
        }
      }
    }),
    upsertEntity: build.mutation({
      query: args => {
        const { id } = args
        // alert(JSON.stringify(args))
        const actualArgs = massageSingleRecordBeforeUpsert(args)
        actualArgs.contacts = actualArgs.fullContacts.map(o => {
          return { id: o.id, role_id: o.role_id }
        })
        actualArgs.owners = actualArgs.fullOwners.map(o => {
          return { id: o.id, role_id: o.role_id }
        })
        delete actualArgs.fullContacts
        delete actualArgs.fullOwners
        delete actualArgs.__permissions__

        if (!actualArgs.addresses[0].unit) actualArgs.addresses[0].unit = null

        //actualArgs.addresses[0].name = 'generic'

        //alert(JSON.stringify(actualArgs.contacts))

        if (id) {
          // update

          return {
            url: `${SLICE_API_ROOT}/${id}`,
            method: 'PUT',
            body: actualArgs
          }
        } //insert
        else {
          return {
            url: SLICE_API_ROOT,
            method: 'POST',
            body: actualArgs
          }
        }
      }
    }),
    deleteEntity: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/${args.id}`,
          method: 'DELETE'
        }
      }
    })
  }),
  overrideExisting: true
})

export const {
  useGetOneFatEntityQuery,
  useGetExecutiveEntityQuickViewQuery,
  useLazyListEntityRolesQuery,
  useLazyListEntityTypesQuery,
  useListEntityTypesQuery,
  useLazyListEntitiesQuery,
  useUpsertEntityMutation,
  useDeleteEntityMutation,
  useBulkUpdateEntitiesMutation
} = entityApi
